import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Avatar, ErrorNote, Loading, cx } from '../components/ui';
import { ApiError, api, type LiveInterview } from '../lib/api';
import { formatDuration, titleCase } from '../lib/format';
import { useNow } from '../lib/hooks';
import type { ObserveGrant } from './ObservePanel';

// livekit-client stays out of the main bundle until someone actually observes.
const ObservePanel = lazy(() => import('./ObservePanel'));

/** The live endpoint returns the whole interview row; these are the extra fields the wall reads. */
type LiveRow = Omit<LiveInterview, 'channel'> & {
  channel: 'WEB' | 'VIDEO' | 'PHONE';
  language?: string | null;
  state?: { sectionsDone?: string[] } | null;
};

type ObserveMode = 'watch' | 'takeover';

const CHANNEL: Record<LiveRow['channel'], { icon: IconName; chip: string }> = {
  VIDEO: { icon: 'video', chip: 'bg-[#d6217f]/25 text-[#f7a6cf]' },
  PHONE: { icon: 'phone', chip: 'bg-[#7e2ea6]/35 text-[#d9b3f0]' },
  WEB: { icon: 'globe', chip: 'bg-white/10 text-white/80' },
};

export function LivePage() {
  const live = useQuery({ queryKey: ['live'], queryFn: () => api.get<LiveRow[]>('/api/interviews/live'), refetchInterval: 3000 });
  const now = useNow();
  // Once the observe endpoint answers 404 we stop offering it for this page view.
  const [observeUnavailable, setObserveUnavailable] = useState(false);

  const rows = live.data ?? [];
  const inProgress = rows.filter((r) => r.status === 'IN_PROGRESS').length;

  return (
    <div className="relative -mx-4 -my-8 min-h-[calc(100vh-4rem)] overflow-hidden bg-night px-4 py-8 text-white sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <span className="drift pointer-events-none absolute -left-32 top-1/4 h-[520px] w-[520px] rounded-full bg-[#d6217f]/15 blur-3xl" aria-hidden />
      <div className="relative mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/50">Live wall</p>
          <h1 className="display mt-2 text-[36px] font-medium leading-none">Happening now</h1>
          <p className="mt-3 text-sm text-white/60">
            <span className="font-semibold text-white">{inProgress}</span> in interview · <span className="font-semibold text-white">{rows.length - inProgress}</span> ringing · refreshes every 3 seconds
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 font-mono text-[11px] text-white/70 ring-1 ring-white/10">
          <span className="live-dot h-2 w-2 rounded-full bg-[#4ade80] shadow-[0_0_10px_#4ade80]" aria-hidden /> Connected
        </span>
      </div>
      {live.isLoading && <Loading />}
      <ErrorNote error={live.error} />
      {live.data && rows.length === 0 && (
        <div className="relative flex flex-col items-center rounded-xl border border-dashed border-white/15 px-6 py-20 text-center">
          <span className="relative mb-6 flex h-16 w-16 items-center justify-center">
            <span className="pulse-ring absolute inset-0 rounded-full bg-[#d6217f]/40" />
            <span className="relative h-4 w-4 rounded-full bg-[#d6217f] shadow-[0_0_20px_#d6217f]" />
          </span>
          <p className="display text-2xl">Nothing live right now</p>
          <p className="mt-2 max-w-sm text-sm text-white/60">Interviews in progress and ringing calls appear here the moment they start.</p>
        </div>
      )}
      <div className="relative grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {rows.map((i) => (
          <LiveTile key={i.id} row={i} now={now} observeUnavailable={observeUnavailable} onUnavailable={() => setObserveUnavailable(true)} />
        ))}
      </div>
    </div>
  );
}

function LiveTile({ row, now, observeUnavailable, onUnavailable }: { row: LiveRow; now: number; observeUnavailable: boolean; onUnavailable: () => void }) {
  const [busy, setBusy] = useState<ObserveMode | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState<{ grant: ObserveGrant; takeOver: boolean } | null>(null);
  const ringing = row.status === 'RINGING';
  const stage = row.state?.sectionsDone?.at(-1);
  const ch = CHANNEL[row.channel];

  async function observe(mode: ObserveMode) {
    if (mode === 'takeover' && !window.confirm(`Take over ${row.candidate.name}'s interview? The AI interviewer stops for good and you speak to the candidate directly.`)) return;
    setBusy(mode);
    setNote(null);
    try {
      const grant = await api.post<ObserveGrant>(`/api/interviews/${row.id}/observe`, { mode });
      setOpen({ grant, takeOver: mode === 'takeover' });
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501)) {
        onUnavailable();
      } else {
        setNote(e instanceof Error ? e.message : 'Could not connect');
      }
    } finally {
      setBusy(null);
    }
  }

  const btn = 'inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <article className="scale-in flex flex-col rounded-lg bg-white/[0.05] p-5 ring-1 ring-white/10 backdrop-blur transition hover:ring-white/20">
      <div className="flex items-center justify-between gap-2">
        <span className={cx('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider', ch.chip)}>
          <Icon name={ch.icon} className="h-3 w-3" />
          {row.channel}
        </span>
        <span className="flex items-center gap-1.5 font-mono text-xs tabular text-white/70">
          <span className={cx('live-dot h-1.5 w-1.5 rounded-full', ringing ? 'bg-[#fbbf24]' : 'bg-[#4ade80]')} aria-hidden />
          {ringing ? 'Ringing' : row.startedAt ? formatDuration(now - new Date(row.startedAt).getTime()) : 'Connecting'}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Avatar name={row.candidate.name} size="md" />
        <div className="min-w-0">
          <Link to={`/candidates/${row.candidate.id}`} className="display block truncate text-xl hover:underline">
            {row.candidate.name}
          </Link>
          <p className="truncate text-sm text-white/55">{row.candidate.job.title}</p>
        </div>
      </div>
      <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-white/45">
        Stage: <span className="text-white/85">{ringing ? 'dialling' : stage ? titleCase(stage) : 'introduction'}</span>
        {row.language ? <> · {row.language}</> : null}
      </p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          className={cx(btn, 'bg-white/10 text-white hover:bg-white/15')}
          disabled={ringing || observeUnavailable || busy !== null}
          onClick={() => observe('watch')}
          title={observeUnavailable ? 'Not available on this server yet' : undefined}
        >
          <Icon name="eye" className="h-3.5 w-3.5" />
          {busy === 'watch' ? 'Connecting…' : 'Watch silently'}
        </button>
        <button
          type="button"
          className={cx(btn, 'bg-brand-gradient text-white shadow-brand hover:brightness-110')}
          disabled={ringing || observeUnavailable || busy !== null}
          onClick={() => observe('takeover')}
          title={observeUnavailable ? 'Not available on this server yet' : undefined}
        >
          <Icon name="mic" className="h-3.5 w-3.5" />
          {busy === 'takeover' ? 'Connecting…' : 'Take over'}
        </button>
      </div>
      {open && (
        <Suspense fallback={null}>
          <ObservePanel grant={open.grant} takeOver={open.takeOver} title={row.candidate.name} interviewId={row.id} onClose={() => setOpen(null)} />
        </Suspense>
      )}
      {(note || observeUnavailable) && <p className="mt-3 text-xs text-white/55">{observeUnavailable ? 'Watching and take-over are not available on this server yet.' : note}</p>}
    </article>
  );
}
